// Dr.Rx™ AI Clinical Pharmacist Co-Pilot Workspace
import React, { useState, useRef, useEffect, useMemo } from 'react';
import type { Message, AnswerLength, ChatSettings, ChatSession, View, ProfessionalFocus, DrugInfo, DosingInfo } from '../types';
import { ChatMessageRole } from '../types';
import AiDisclaimer from './AiDisclaimer';
import ChatSidebar from './ChatSidebar';
import ChatSettingsModal from './ChatSettingsModal';
import AddDrugModal from './AddDrugModal';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { getAllDrugs } from '../services/pharmacyService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

// --- ICONS ---
const SendIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.428A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" /></svg>;
const HistoryIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const SettingsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
const CopyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>;
const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>;
const RegenerateIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M4 4l1.5 1.5A9 9 0 0120.5 15M20 20l-1.5-1.5A9 9 0 003.5 9" /></svg>;
const PlusCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" /></svg>;
const ToolIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" /></svg>;
const MicrophoneIcon: React.FC<{ isListening?: boolean }> = ({ isListening }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 transition-colors ${isListening ? 'text-red-500 animate-pulse' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
);
const BookOpenIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
);
const ExportNoteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
);
const SparklesIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
);

const SmallLogoIcon = () => (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="5" fill="#FACC15" />
        <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2" />
        <circle cx="24" cy="7" r="2" fill="white" />
        <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2" />
        <circle cx="24" cy="41" r="2" fill="white" />
        <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2" />
        <circle cx="7" cy="24" r="2" fill="white" />
        <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2" />
        <circle cx="41" cy="24" r="2" fill="white" />
    </svg>
);

const QUICK_TEMPLATES = [
    {
        id: 'renal',
        label: 'Renal Dosing',
        badge: 'CrCl / HD',
        prompt: 'What is the CrCl-based renal dose adjustment, hemodialysis supplemental dosing, and administration guidance for ',
        placeholder: 'e.g., apixaban, vancomycin, cefepime...'
    },
    {
        id: 'ddi',
        label: 'DDI & CYP Check',
        badge: 'Interactions',
        prompt: 'Analyze clinically significant drug-drug interactions, CYP450/P-gp mechanisms, severity, and management for: ',
        placeholder: 'e.g., paxlovid + tacrolimus...'
    },
    {
        id: 'iv',
        label: 'Y-Site IV Check',
        badge: 'IV Stability',
        prompt: 'IV compatibility (Y-site), concentration dependency, and diluent stability for: ',
        placeholder: 'e.g., vancomycin + piperacillin-tazobactam...'
    },
    {
        id: 'bridging',
        label: 'Anticoag Bridging',
        badge: 'Peri-Op',
        prompt: 'Perioperative anticoagulation hold timeline, bridging indication, and resumption protocol for: ',
        placeholder: 'e.g., warfarin or Eliquis prior to spinal surgery...'
    },
    {
        id: 'peds',
        label: 'Pediatric Dosing',
        badge: 'mg/kg/day',
        prompt: 'Weight-based pediatric dosing (mg/kg/day), maximum daily dose, and suspension concentration options for: ',
        placeholder: 'e.g., amoxicillin in acute otitis media (18 kg)...'
    },
    {
        id: 'preg',
        label: 'Pregnancy & Lactation',
        badge: 'OB / LactMed',
        prompt: 'FDA pregnancy & lactation safety profile, trimester-specific risks, RID%, and preferred alternatives for: ',
        placeholder: 'e.g., labetalol vs nifedipine in gestational HTN...'
    },
];

const ROLES: ProfessionalFocus[] = ['Pharmacist', 'Physician', 'Nurse', 'Researcher'];

interface ChatProps {
    messages: Message[];
    onSendMessage: (message: string, answerLength: AnswerLength) => void;
    onRegenerate: () => void;
    isLoading: boolean;
    settings: ChatSettings;
    onSettingsChange: (newSettings: ChatSettings) => void;
    sessions: ChatSession[];
    activeSessionId: string | null;
    onSelectSession: (id: string) => void;
    onNewChat: () => void;
    onDeleteSession: (id: string) => void;
    onRenameSession: (id: string, newTitle: string) => void;
    onTogglePinSession?: (id: string) => void;
    onClearAllSessions: () => void;
    onDrugAdded: () => void;
    setActiveTool: (tool: string | null) => void;
    onNavigate: (view: View) => void;
    onSelectDrug?: (drugId: string) => void;
}

const renderFormattedText = (text: string) => {
    const cleanedText = text
        .replace(/DRR_FOLLOW_UP:\[.*?\](\s*)/s, '')
        .replace(/DRR_RECOMMEND_TOOL:\[.*?\](\s*)/s, '')
        .replace(/DRR_ADD_DRUG:\[.*?\](\s*)/s, '')
        .trim();

    const blocks = cleanedText.split(/\n\s*\n/);

    return blocks.map((block, blockIndex) => {
        const trimmedBlock = block.trim();
        if (!trimmedBlock) return null;

        // 1. Headers (### Header)
        if (trimmedBlock.startsWith('### ')) {
            const headerText = trimmedBlock.replace('### ', '');
            const isWarningHeader = /warning|contraindication|safety/i.test(headerText);
            return (
                <h3
                    key={blockIndex}
                    className={`text-sm font-bold uppercase tracking-wider mt-5 mb-2 first:mt-0 border-b pb-1.5 flex items-center gap-2 ${
                        isWarningHeader
                            ? 'text-red-300 border-red-500/40'
                            : 'text-yellow-400 border-blue-800/70'
                    }`}
                >
                    {headerText}
                </h3>
            );
        }

        // 2. Unordered Lists
        const lines = trimmedBlock.split('\n');
        const isUnorderedList = lines.every(line => line.trim().startsWith('* ') || line.trim().startsWith('- '));
        if (isUnorderedList) {
            return (
                <ul key={blockIndex} className="space-y-1.5 my-2.5 pl-4 border-l-2 border-blue-800/50">
                    {lines.map((line, lineIndex) => {
                        const itemContent = line.trim().substring(2);
                        return (
                            <li key={lineIndex} className="text-gray-200 text-sm leading-relaxed">
                                {formatInlineBold(itemContent)}
                            </li>
                        );
                    })}
                </ul>
            );
        }

        // 3. Ordered Lists
        const isOrderedList = lines.every(line => /^\d+\.\s/.test(line.trim()));
        if (isOrderedList) {
            return (
                <ol key={blockIndex} className="list-decimal list-inside space-y-1.5 my-2.5 pl-1 text-sm text-gray-300">
                    {lines.map((line, lineIndex) => {
                        const itemContent = line.trim().replace(/^\d+\.\s/, '');
                        return (
                            <li key={lineIndex} className="leading-relaxed">
                                {formatInlineBold(itemContent)}
                            </li>
                        );
                    })}
                </ol>
            );
        }

        // 4. Warning Badges Callout Detection
        const hasWarningTrigger = /^(Black Box Warning:|Contraindications:|Severe Interaction:|Warnings:)/i.test(trimmedBlock);
        if (hasWarningTrigger) {
            return (
                <div
                    key={blockIndex}
                    className="my-3 p-3.5 rounded-xl bg-red-950/50 border border-red-500/60 text-red-100 text-sm leading-relaxed shadow-sm"
                >
                    {formatInlineBold(trimmedBlock)}
                </div>
            );
        }

        // 5. Standard Paragraphs
        return (
            <p key={blockIndex} className="my-2.5 leading-relaxed text-gray-200 text-sm">
                {lines.map((line, lineIndex) => (
                    <React.Fragment key={lineIndex}>
                        {formatInlineBold(line)}
                        {lineIndex < lines.length - 1 && <br />}
                    </React.Fragment>
                ))}
            </p>
        );
    });
};

const formatInlineBold = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return (
                <strong key={index} className="font-bold text-white">
                    {part.slice(2, -2)}
                </strong>
            );
        }
        return part;
    });
};

const SourceBadge: React.FC<{ source?: 'local' | 'gemini' }> = ({ source }) => {
    if (!source) return null;
    const isLocal = source === 'local';
    const text = isLocal ? 'Verified Drug Index' : 'Dr.Rx™ Clinical Engine';
    const color = isLocal
        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
        : 'bg-blue-500/15 text-blue-300 border-blue-500/30';

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-bold border ${color}`}>
            <span className={`w-1.5 h-1.5 mr-1.5 rounded-full ${isLocal ? 'bg-emerald-400' : 'bg-blue-400'}`} />
            {text}
        </span>
    );
};

const GroundingSources: React.FC<{ metadata: any }> = ({ metadata }) => {
    if (!metadata || !metadata.groundingChunks || metadata.groundingChunks.length === 0) {
        return null;
    }

    const webSources = metadata.groundingChunks.filter((chunk: any) => chunk.web && chunk.web.uri && chunk.web.title);
    if (webSources.length === 0) return null;

    return (
        <div className="mt-4 pt-3 border-t border-blue-800/50">
            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Verified Clinical & Web Citations</h4>
            <ol className="list-decimal list-inside text-xs space-y-1">
                {webSources.map((chunk: any, index: number) => (
                    <li key={index} className="truncate text-gray-400">
                        <a
                            href={chunk.web.uri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-yellow-300 hover:underline"
                            title={chunk.web.title}
                        >
                            {chunk.web.title}
                        </a>
                    </li>
                ))}
            </ol>
        </div>
    );
};

const ChatMessage: React.FC<{
    message: Message;
    isLastMessage: boolean;
    onCopy: (text: string, id: string) => void;
    copiedMessageId: string | null;
    onRegenerate: () => void;
    onOpenAddDrugModal: (drugName: string) => void;
    onSelectTool: (toolName: string) => void;
    onSendMessage: (prompt: string) => void;
    onOpenInlineDrug: (drugId: string) => void;
}> = ({
    message,
    isLastMessage,
    onCopy,
    copiedMessageId,
    onRegenerate,
    onOpenAddDrugModal,
    onSelectTool,
    onSendMessage,
    onOpenInlineDrug,
}) => {
    const isModel = message.role === ChatMessageRole.MODEL;
    const isCopied = copiedMessageId === message.id;

    // Collect recommended tools from both new array and legacy action field
    const recommendedTools = useMemo(() => {
        const list = new Set<string>();
        if (message.recommendedTools) {
            message.recommendedTools.forEach(t => list.add(t));
        }
        if (message.action?.type === 'RECOMMEND_TOOL') {
            if (message.action.toolNames) message.action.toolNames.forEach(t => list.add(t));
            else if (message.action.toolName) list.add(message.action.toolName);
        }
        return Array.from(list);
    }, [message]);

    const addDrugName = message.addDrugName || (message.action?.type === 'ADD_DRUG' ? message.action.drugName : undefined);

    if (isModel && message.text === '' && !message.blackBoxWarning) {
        return null;
    }

    return (
        <div className={`flex flex-col ${isModel ? 'items-start' : 'items-end'}`}>
            <div className={`flex items-start gap-3 max-w-2xl w-full ${isModel ? 'justify-start' : 'justify-end'}`}>
                {isModel && (
                    <div className="w-8 h-8 rounded-xl bg-[#1E2E4A] border border-yellow-400/40 flex items-center justify-center flex-shrink-0 font-bold text-yellow-400 text-xs mt-1 shadow-md">
                        Rx
                    </div>
                )}
                <div
                    className={`rounded-2xl p-4 sm:p-5 border transition-all ${
                        isModel
                            ? 'bg-[#15243E]/95 border-blue-900/60 text-gray-200 w-full shadow-lg'
                            : 'bg-yellow-400 border-yellow-300 text-gray-950 font-semibold max-w-lg shadow-md'
                    }`}
                >
                    {isModel && (
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-blue-900/40">
                            <SourceBadge source={message.source} />
                            {message.matchedLocalDrugId && (
                                <button
                                    onClick={() => onOpenInlineDrug(message.matchedLocalDrugId!)}
                                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-yellow-400/15 hover:bg-yellow-400/25 text-yellow-300 border border-yellow-400/40 px-2.5 py-1 rounded-lg transition-colors"
                                >
                                    <BookOpenIcon />
                                    <span>View {message.matchedLocalDrugName || 'Monograph'} Card</span>
                                </button>
                            )}
                        </div>
                    )}
                    {message.blackBoxWarning && (
                        <div className="mb-4 p-3.5 rounded-xl bg-red-950/60 border-2 border-red-500/80">
                            <h4 className="font-bold text-red-200 text-xs uppercase tracking-wider flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.636-1.214 2.852-1.214 3.488 0l6.248 11.921A2 2 0 0116.248 18H3.752a2 2 0 01-1.745-3.02L8.257 3.1zM10 14a1 1 0 100-2 1 1 0 000 2zm-1-4a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                                FDA Black Box Warning
                            </h4>
                            <p className="text-xs text-red-100/90 mt-1.5 leading-relaxed">{message.blackBoxWarning}</p>
                        </div>
                    )}
                    <div className="prose prose-invert max-w-none">{renderFormattedText(message.text)}</div>
                    {isModel && <GroundingSources metadata={message.groundingMetadata} />}

                    {/* Interactive Multi-Tool Launcher & Add Drug Bar */}
                    {isModel && (recommendedTools.length > 0 || addDrugName) && (
                        <div className="mt-4 pt-3.5 border-t border-blue-800/60 space-y-2.5">
                            {recommendedTools.length > 0 && (
                                <div>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                                        Recommended Clinical Calculators & Tools
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {recommendedTools.map(tool => (
                                            <button
                                                key={tool}
                                                onClick={() => onSelectTool(tool)}
                                                className="inline-flex items-center bg-yellow-400 hover:bg-yellow-300 text-gray-950 font-bold py-1.5 px-3 rounded-lg transition-all text-xs shadow-sm"
                                            >
                                                <ToolIcon /> Open {tool}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {addDrugName && (
                                <div className="flex items-center justify-between gap-2 bg-[#101D35] p-2.5 rounded-xl border border-blue-900/50">
                                    <span className="text-xs text-gray-300">
                                        Save <strong className="text-white">{addDrugName}</strong> to your personal Drug Index?
                                    </span>
                                    <button
                                        onClick={() => onOpenAddDrugModal(addDrugName)}
                                        className="inline-flex items-center bg-blue-600 hover:bg-blue-500 text-white font-bold py-1.5 px-3 rounded-lg transition-colors text-xs flex-shrink-0"
                                    >
                                        <PlusCircleIcon /> Add Drug
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {isModel && message.text && (
                <div className="pl-11 mt-1.5 flex items-center gap-3 text-gray-400">
                    <button
                        onClick={() => onCopy(message.text, message.id)}
                        className="flex items-center gap-1 text-xs hover:text-white transition-colors py-1 px-2 rounded-lg hover:bg-white/5"
                    >
                        {isCopied ? (
                            <>
                                <CheckIcon /> <span className="text-emerald-400 font-semibold">Copied Note</span>
                            </>
                        ) : (
                            <>
                                <CopyIcon /> <span>Copy Clinical Note</span>
                            </>
                        )}
                    </button>
                    {isLastMessage && (
                        <button
                            onClick={onRegenerate}
                            className="flex items-center gap-1 text-xs hover:text-white transition-colors py-1 px-2 rounded-lg hover:bg-white/5"
                        >
                            <RegenerateIcon /> <span>Regenerate</span>
                        </button>
                    )}
                </div>
            )}

            {isModel && message.followUpPrompts && message.followUpPrompts.length > 0 && isLastMessage && (
                <div className="pl-11 mt-3 flex flex-wrap items-center gap-2 max-w-2xl">
                    {message.followUpPrompts.map((prompt, index) => (
                        <button
                            key={index}
                            onClick={() => onSendMessage(prompt)}
                            className="text-xs text-left bg-[#1E2E4A]/90 text-blue-200 border border-blue-800/60 px-3 py-2 rounded-xl hover:bg-blue-800/70 hover:border-yellow-400/50 hover:text-white transition-all"
                        >
                            {prompt}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const Chat: React.FC<ChatProps> = ({
    messages,
    onSendMessage,
    onRegenerate,
    isLoading,
    settings,
    onSettingsChange,
    sessions,
    activeSessionId,
    onSelectSession,
    onNewChat,
    onDeleteSession,
    onRenameSession,
    onTogglePinSession,
    onClearAllSessions,
    onDrugAdded,
    setActiveTool,
    onNavigate,
    onSelectDrug,
}) => {
    const { uid } = useAuth();
    const { showToast } = useToast();
    const [input, setInput] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
    const [drugToAdd, setDrugToAdd] = useState<string | null>(null);
    const [showTemplatesBar, setShowTemplatesBar] = useState(false);

    // Inline Drug Lookup Drawer State
    const [isDrugDrawerOpen, setIsDrugDrawerOpen] = useState(false);
    const [drugSearchQuery, setDrugSearchQuery] = useState('');
    const [allDrugs, setAllDrugs] = useState<DrugInfo[]>([]);
    const [selectedInlineDrug, setSelectedInlineDrug] = useState<DrugInfo | null>(null);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const { isListening, transcript, startListening, stopListening, hasRecognitionSupport: isSupported } = useSpeechRecognition();

    useEffect(() => {
        if (transcript) {
            setInput(transcript);
        }
    }, [transcript]);

    useEffect(() => {
        if (!uid) return;
        getAllDrugs(uid).then(setAllDrugs).catch(console.error);
    }, [uid]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(scrollToBottom, [messages, isLoading]);

    const activeAnswerLength: AnswerLength = settings.answerLength || 'short';

    const handleSend = (e: React.FormEvent) => {
        e.preventDefault();
        if (isListening) stopListening();
        if (input.trim() && !isLoading) {
            onSendMessage(input.trim(), activeAnswerLength);
            setInput('');
            setShowTemplatesBar(false);
        }
    };

    const handleTemplateSelect = (template: typeof QUICK_TEMPLATES[0]) => {
        setInput(template.prompt);
        setShowTemplatesBar(false);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 50);
    };

    const handleRoleSelect = (role: ProfessionalFocus) => {
        onSettingsChange({ ...settings, professionalFocus: role });
        showToast(`Calibrated clinical depth for ${role} focus.`, 'info', 2000);
    };

    const handleLengthChange = (length: AnswerLength) => {
        onSettingsChange({ ...settings, answerLength: length });
    };

    const handleCopy = (text: string, id: string) => {
        const cleanedText = text
            .replace(/DRR_FOLLOW_UP:\[.*?\](\s*)/s, '')
            .replace(/DRR_RECOMMEND_TOOL:\[.*?\](\s*)/s, '')
            .replace(/DRR_ADD_DRUG:\[.*?\](\s*)/s, '')
            .trim();
        navigator.clipboard.writeText(cleanedText);
        setCopiedMessageId(id);
        showToast('Clinical note copied to clipboard.', 'success', 2000);
        setTimeout(() => setCopiedMessageId(null), 2000);
    };

    const handleExportCurrentSession = () => {
        if (messages.length === 0) {
            showToast('No messages in the current consultation to export.', 'warning');
            return;
        }
        const activeSession = sessions.find(s => s.id === activeSessionId);
        const title = activeSession?.title || 'Clinical Consultation';
        const lines: string[] = [
            `# Dr.Rx™ Clinical Consultation Note: ${title}`,
            `Date: ${new Date().toLocaleString()}`,
            `Clinician Focus: ${settings.professionalFocus} | Mode: ${activeAnswerLength.toUpperCase()}`,
            '---',
            '',
        ];
        messages.forEach(m => {
            const clean = m.text
                .replace(/DRR_FOLLOW_UP:\[.*?\](\s*)/s, '')
                .replace(/DRR_RECOMMEND_TOOL:\[.*?\](\s*)/s, '')
                .replace(/DRR_ADD_DRUG:\[.*?\](\s*)/s, '')
                .trim();
            lines.push(`### ${m.role === ChatMessageRole.USER ? 'Clinician Query' : 'Dr.Rx™ Clinical Assessment'}`);
            if (m.blackBoxWarning) {
                lines.push(`> **BLACK BOX WARNING:** ${m.blackBoxWarning}`);
                lines.push('');
            }
            lines.push(clean);
            lines.push('');
        });

        const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DrRx_Note_${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast('Consultation exported as Clinical Note (.md).', 'success');
    };

    const handleOpenInlineDrug = (drugId: string) => {
        const found = allDrugs.find(d => d.id === drugId);
        if (found) {
            setSelectedInlineDrug(found);
            setIsDrugDrawerOpen(true);
        }
    };

    const filteredDrawerDrugs = useMemo(() => {
        const q = drugSearchQuery.trim().toLowerCase();
        if (!q) return allDrugs.slice(0, 25);
        return allDrugs.filter(
            d =>
                d.brandName.toLowerCase().includes(q) ||
                d.genericName.toLowerCase().includes(q) ||
                d.therapeuticClass.toLowerCase().includes(q)
        );
    }, [allDrugs, drugSearchQuery]);

    const handleDrugAddedAndClose = () => {
        onDrugAdded();
        setDrugToAdd(null);
        if (uid) {
            getAllDrugs(uid).then(setAllDrugs).catch(console.error);
        }
    };

    const handleMicClick = () => {
        if (isListening) {
            stopListening();
        } else {
            startListening();
        }
    };

    return (
        <div className="flex h-[calc(100vh-140px)] relative overflow-hidden">
            <ChatSidebar
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                sessions={sessions}
                activeSessionId={activeSessionId}
                onSelectSession={id => {
                    onSelectSession(id);
                    setIsSidebarOpen(false);
                }}
                onNewChat={() => {
                    onNewChat();
                    setIsSidebarOpen(false);
                }}
                onDeleteSession={onDeleteSession}
                onRenameSession={onRenameSession}
                onTogglePinSession={onTogglePinSession}
            />

            {/* Inline Drug Monograph Lookup Drawer */}
            {isDrugDrawerOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
                    onClick={() => setIsDrugDrawerOpen(false)}
                >
                    <div
                        className="bg-[#111E36] border-l border-blue-900/60 w-full max-w-md h-full flex flex-col p-5 text-gray-200 shadow-2xl overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between pb-3 mb-4 border-b border-blue-900/60">
                            <div className="flex items-center gap-2">
                                <BookOpenIcon />
                                <h3 className="text-lg font-bold text-white">Inline Drug Monograph Lookup</h3>
                            </div>
                            <button
                                onClick={() => setIsDrugDrawerOpen(false)}
                                className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/5"
                            >
                                ✕
                            </button>
                        </div>

                        {selectedInlineDrug ? (
                            <div className="flex-grow overflow-y-auto custom-scrollbar space-y-4 pr-1">
                                <button
                                    onClick={() => setSelectedInlineDrug(null)}
                                    className="text-xs font-bold text-yellow-400 hover:text-yellow-300 flex items-center gap-1"
                                >
                                    ← Search Another Medication
                                </button>

                                <div className="bg-[#15243E] p-4 rounded-xl border border-blue-900/60">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <h4 className="text-2xl font-bold text-white">{selectedInlineDrug.brandName}®</h4>
                                            <p className="text-sm text-gray-400 font-medium">{selectedInlineDrug.genericName}</p>
                                        </div>
                                        {selectedInlineDrug.hasBlackBoxWarning && (
                                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">
                                                BBW
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-yellow-300/90 mt-2 font-semibold">{selectedInlineDrug.therapeuticClass}</p>
                                    <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-blue-900/40 text-xs">
                                        <div>
                                            <span className="text-gray-500 block">Forms</span>
                                            <span className="text-gray-200 font-medium">{selectedInlineDrug.dosageForm}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block">Strengths</span>
                                            <span className="text-gray-200 font-medium tabular-nums">{selectedInlineDrug.strength}</span>
                                        </div>
                                    </div>
                                </div>

                                {selectedInlineDrug.hasBlackBoxWarning && selectedInlineDrug.blackBoxWarning && (
                                    <div className="bg-red-950/50 border border-red-500/60 p-3.5 rounded-xl text-xs text-red-100 leading-relaxed">
                                        <strong className="text-red-300 block uppercase tracking-wider mb-1">Black Box Warning</strong>
                                        {selectedInlineDrug.blackBoxWarning}
                                    </div>
                                )}

                                <div className="bg-[#15243E] p-4 rounded-xl border border-blue-900/60 space-y-2">
                                    <h5 className="text-xs font-bold uppercase tracking-wider text-yellow-400">Dosing & Administration</h5>
                                    {Array.isArray(selectedInlineDrug.dosingAndAdministration) ? (
                                        <div className="space-y-2 text-xs">
                                            {(selectedInlineDrug.dosingAndAdministration as DosingInfo[]).map((d, idx) => (
                                                <div key={idx} className="p-2.5 rounded-lg bg-black/20 border border-blue-900/30">
                                                    <div className="flex justify-between font-bold text-white mb-0.5">
                                                        <span>{d.indication}</span>
                                                        <span className="text-yellow-300">{d.population}</span>
                                                    </div>
                                                    <p className="text-gray-300">{d.dose}</p>
                                                    {d.notes && <p className="text-gray-400 italic mt-1">{d.notes}</p>}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-300 leading-relaxed">
                                            {selectedInlineDrug.dosingAndAdministration || 'N/A'}
                                        </p>
                                    )}
                                </div>

                                {selectedInlineDrug.contraindications && (
                                    <div className="bg-[#15243E] p-4 rounded-xl border border-blue-900/60">
                                        <h5 className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-1">Contraindications</h5>
                                        <p className="text-xs text-gray-300 leading-relaxed">{selectedInlineDrug.contraindications}</p>
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-2 pt-2">
                                    <button
                                        onClick={() => {
                                            setIsDrugDrawerOpen(false);
                                            onSendMessage(
                                                `Provide a ${settings.professionalFocus}-focused clinical summary, renal/hepatic adjustments, and key counseling pearls for ${selectedInlineDrug.brandName} (${selectedInlineDrug.genericName}).`,
                                                activeAnswerLength
                                            );
                                        }}
                                        className="flex-1 bg-yellow-400 hover:bg-yellow-300 text-gray-950 font-bold py-2.5 px-3 rounded-xl text-xs transition-colors"
                                    >
                                        Ask Co-Pilot About {selectedInlineDrug.brandName}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsDrugDrawerOpen(false);
                                            setActiveTool('Dose Adjust');
                                        }}
                                        className="bg-[#1E2E4A] hover:bg-blue-800 text-white font-semibold py-2.5 px-3 rounded-xl text-xs border border-blue-800/60 transition-colors"
                                    >
                                        Dose Adjust Tool
                                    </button>
                                    {onSelectDrug && (
                                        <button
                                            onClick={() => {
                                                setIsDrugDrawerOpen(false);
                                                onSelectDrug(selectedInlineDrug.id);
                                            }}
                                            className="w-full bg-black/30 hover:bg-white/10 text-gray-300 font-semibold py-2 px-3 rounded-xl text-xs border border-blue-900/50 transition-colors"
                                        >
                                            Open Full Monograph View →
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex-grow flex flex-col overflow-hidden">
                                <input
                                    type="text"
                                    value={drugSearchQuery}
                                    onChange={e => setDrugSearchQuery(e.target.value)}
                                    placeholder="Search Top 100 & custom medications..."
                                    className="w-full bg-[#0B1426] border border-blue-900/60 rounded-xl p-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-yellow-400 mb-3"
                                    autoFocus
                                />
                                <div className="flex-grow overflow-y-auto custom-scrollbar space-y-2 pr-1">
                                    {filteredDrawerDrugs.map(drug => (
                                        <button
                                            key={drug.id}
                                            onClick={() => setSelectedInlineDrug(drug)}
                                            className="w-full text-left p-3 rounded-xl bg-[#15243E] hover:bg-[#1E2E4A] border border-blue-900/50 hover:border-yellow-400/50 transition-all flex items-center justify-between gap-2"
                                        >
                                            <div className="min-w-0">
                                                <p className="font-bold text-white text-sm truncate">
                                                    {drug.brandName} <span className="text-gray-400 font-normal">({drug.genericName})</span>
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">{drug.therapeuticClass}</p>
                                            </div>
                                            {drug.hasBlackBoxWarning && (
                                                <span className="text-[10px] font-bold bg-red-500/20 text-red-300 border border-red-500/30 px-2 py-0.5 rounded flex-shrink-0">
                                                    BBW
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="flex flex-col flex-grow h-full min-w-0">
                {/* Powerhouse Clinical Workspace Header */}
                <header className="flex flex-col gap-2.5 pb-3 border-b border-blue-900/60 flex-shrink-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                            <button
                                onClick={() => setIsSidebarOpen(true)}
                                className="flex items-center gap-1.5 px-2.5 py-2 text-xs font-bold text-gray-300 hover:text-white bg-[#15243E] hover:bg-[#1E2E4A] border border-blue-900/60 rounded-xl transition-colors"
                                title="Consultations & Search"
                            >
                                <HistoryIcon />
                                <span className="hidden sm:inline">Consults</span>
                            </button>
                            <button
                                onClick={() => {
                                    setSelectedInlineDrug(null);
                                    setIsDrugDrawerOpen(true);
                                }}
                                className="flex items-center gap-1.5 px-2.5 py-2 text-xs font-bold text-yellow-300 hover:text-yellow-200 bg-[#15243E] hover:bg-[#1E2E4A] border border-yellow-400/30 rounded-xl transition-colors"
                                title="Inline Drug Monograph Lookup"
                            >
                                <BookOpenIcon />
                                <span className="hidden md:inline">Drug Lookup</span>
                            </button>
                        </div>

                        <button
                            onClick={() => onNavigate('dashboard')}
                            className="flex items-center gap-2 hover:opacity-90 transition-opacity cursor-pointer"
                            title="Return to Dashboard"
                        >
                            <SmallLogoIcon />
                            <h1 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                                Dr.<span className="text-yellow-400">Rx</span>
                                <sup className="text-[10px] -top-2">TM</sup>{' '}
                                <span className="text-gray-400 font-normal text-sm hidden sm:inline">Clinical Co-Pilot</span>
                            </h1>
                        </button>

                        <div className="flex items-center gap-1.5">
                            {messages.length > 0 && (
                                <button
                                    onClick={handleExportCurrentSession}
                                    className="flex items-center gap-1.5 px-2.5 py-2 text-xs font-bold text-gray-300 hover:text-white bg-[#15243E] hover:bg-[#1E2E4A] border border-blue-900/60 rounded-xl transition-colors"
                                    title="Export Consultation as Clinical Note (.md)"
                                >
                                    <ExportNoteIcon />
                                    <span className="hidden lg:inline">Export Note</span>
                                </button>
                            )}
                            <button
                                onClick={() => setIsSettingsOpen(true)}
                                className="p-2 text-gray-400 hover:text-white bg-[#15243E] hover:bg-[#1E2E4A] border border-blue-900/60 rounded-xl transition-colors"
                                aria-label="Open chat settings"
                                title="Co-Pilot Settings"
                            >
                                <SettingsIcon />
                            </button>
                        </div>
                    </div>

                    {/* Live Role & Output Mode Calibration Bar */}
                    <div className="flex items-center justify-between gap-2 overflow-x-auto no-scrollbar pt-0.5">
                        <div className="flex items-center gap-1 bg-[#111E36] p-1 rounded-xl border border-blue-900/50 flex-shrink-0">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 px-2 hidden sm:inline">
                                Lens:
                            </span>
                            {ROLES.map(role => {
                                const active = settings.professionalFocus === role;
                                return (
                                    <button
                                        key={role}
                                        type="button"
                                        onClick={() => handleRoleSelect(role)}
                                        className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                            active
                                                ? 'bg-yellow-400 text-gray-950 shadow-sm'
                                                : 'text-gray-400 hover:text-white'
                                        }`}
                                    >
                                        {role}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex items-center gap-1 bg-[#111E36] p-1 rounded-xl border border-blue-900/50 flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => handleLengthChange('short')}
                                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                    activeAnswerLength === 'short'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-400 hover:text-white'
                                }`}
                            >
                                Short (&lt;150w)
                            </button>
                            <button
                                type="button"
                                onClick={() => handleLengthChange('detailed')}
                                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                    activeAnswerLength === 'detailed'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-400 hover:text-white'
                                }`}
                            >
                                Monograph
                            </button>
                        </div>
                    </div>
                </header>

                {/* Main Consultation Stream */}
                <main className="flex-grow overflow-y-auto p-3 sm:p-4 space-y-5 custom-scrollbar">
                    {messages.length === 0 && !isLoading ? (
                        <div className="max-w-3xl mx-auto py-4 sm:py-8">
                            <div className="text-center mb-6">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/10 border border-yellow-400/30 text-yellow-300 text-xs font-bold mb-3">
                                    <SparklesIcon />
                                    <span>Calibrated for {settings.professionalFocus} Decision Support</span>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                                    Point-of-Care Clinical Co-Pilot
                                </h2>
                                <p className="text-xs sm:text-sm text-gray-400 mt-1.5 max-w-xl mx-auto">
                                    Evidence-based dosing, CYP/P-gp interaction pathways, Y-site compatibility, and guideline-directed therapy—zero conversational filler.
                                </p>
                            </div>

                            {/* Quick Clinical Templates Grid */}
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-2.5">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">
                                        Quick Clinical Templates
                                    </h3>
                                    <span className="text-[11px] text-gray-500">Tap to pre-fill clinical query</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                                    {QUICK_TEMPLATES.map(tpl => (
                                        <button
                                            key={tpl.id}
                                            onClick={() => handleTemplateSelect(tpl)}
                                            className="text-left p-3.5 rounded-xl bg-[#15243E]/90 hover:bg-[#1E2E4A] border border-blue-900/60 hover:border-yellow-400/60 transition-all group shadow-sm"
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-bold text-white text-sm group-hover:text-yellow-300 transition-colors">
                                                    {tpl.label}
                                                </span>
                                                <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-blue-950 text-blue-300 border border-blue-800/50">
                                                    {tpl.badge}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-400 line-clamp-2">{tpl.placeholder}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Instant 1-Tap Clinical Vignettes */}
                            <div>
                                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2.5">
                                    High-Yield Point-of-Care Queries
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {[
                                        'What is the renal dosing for apixaban in NVAF, and can it be crushed?',
                                        'Y-site IV compatibility: Vancomycin (4 mg/mL) + Piperacillin-Tazobactam',
                                        'How do I transition a patient from IV heparin infusion to oral rivaroxaban?',
                                        'Compare Empagliflozin vs Dapagliflozin GDMT dosing in HFrEF and CKD.',
                                    ].map((q, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => onSendMessage(q, activeAnswerLength)}
                                            className="p-3 bg-[#111E36] hover:bg-[#1E2E4A] rounded-xl text-left text-xs text-gray-300 hover:text-white transition-colors border border-blue-900/40 hover:border-blue-700"
                                        >
                                            “{q}”
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {messages.map((msg, index) => (
                                <ChatMessage
                                    key={msg.id}
                                    message={msg}
                                    isLastMessage={index === messages.length - 1 && !isLoading}
                                    onCopy={handleCopy}
                                    copiedMessageId={copiedMessageId}
                                    onRegenerate={onRegenerate}
                                    onOpenAddDrugModal={drugName => setDrugToAdd(drugName)}
                                    onSelectTool={toolName => setActiveTool(toolName)}
                                    onSendMessage={prompt => onSendMessage(prompt, activeAnswerLength)}
                                    onOpenInlineDrug={handleOpenInlineDrug}
                                />
                            ))}
                        </>
                    )}

                    {isLoading && (
                        <div className="flex justify-start items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-[#1E2E4A] border border-yellow-400/40 flex items-center justify-center flex-shrink-0 font-bold text-yellow-400 text-xs">
                                Rx
                            </div>
                            <div className="bg-[#15243E] border border-blue-900/60 rounded-2xl p-3.5 text-gray-300 inline-flex items-center gap-2">
                                <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                                <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse delay-150" />
                                <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse delay-300" />
                                <span className="text-xs text-gray-400 ml-1 font-medium">
                                    Synthesizing clinical evidence ({settings.professionalFocus} focus)...
                                </span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </main>

                {/* Composer & Quick Template Drawer */}
                <footer className="pt-2.5 border-t border-blue-900/60 flex-shrink-0">
                    {showTemplatesBar && (
                        <div className="mb-2.5 p-2.5 rounded-xl bg-[#111E36] border border-blue-900/60 flex items-center gap-2 overflow-x-auto no-scrollbar">
                            <span className="text-[11px] font-bold text-yellow-400 uppercase tracking-wider flex-shrink-0 px-1">
                                Templates:
                            </span>
                            {QUICK_TEMPLATES.map(tpl => (
                                <button
                                    key={tpl.id}
                                    type="button"
                                    onClick={() => handleTemplateSelect(tpl)}
                                    className="px-2.5 py-1.5 rounded-lg bg-[#1E2E4A] hover:bg-blue-800 text-xs font-semibold text-gray-200 hover:text-white border border-blue-800/50 whitespace-nowrap transition-colors"
                                >
                                    + {tpl.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <form onSubmit={handleSend} className="relative flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setShowTemplatesBar(prev => !prev)}
                            className={`p-3 rounded-xl border transition-colors flex-shrink-0 ${
                                showTemplatesBar
                                    ? 'bg-yellow-400 text-gray-950 border-yellow-300 font-bold'
                                    : 'bg-[#15243E] text-yellow-400 border-blue-900/60 hover:bg-[#1E2E4A]'
                            }`}
                            title="Toggle Quick Clinical Templates"
                            aria-label="Toggle Quick Clinical Templates"
                        >
                            <SparklesIcon />
                        </button>

                        <div className="relative flex-grow">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                placeholder={
                                    isListening
                                        ? 'Listening for clinical query...'
                                        : `Ask a ${settings.professionalFocus.toLowerCase()} clinical question (dosing, CrCl, DDI, guidelines)...`
                                }
                                className={`w-full bg-[#15243E] border border-[#3A4A67] rounded-xl py-3 pl-4 pr-24 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all ${
                                    isListening ? 'ring-2 ring-red-500 bg-red-950/20' : ''
                                }`}
                                disabled={isLoading}
                            />
                            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                {isSupported && (
                                    <button
                                        type="button"
                                        onClick={handleMicClick}
                                        disabled={isLoading}
                                        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                                        title={isListening ? 'Stop listening' : 'Voice dictation'}
                                    >
                                        <MicrophoneIcon isListening={isListening} />
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    disabled={isLoading || !input.trim()}
                                    className="bg-yellow-400 text-gray-950 rounded-lg p-2 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed hover:bg-yellow-300 transition-colors shadow-sm"
                                    aria-label="Send clinical query"
                                >
                                    <SendIcon />
                                </button>
                            </div>
                        </div>
                    </form>
                    <div className="mt-2">
                        <AiDisclaimer />
                    </div>
                </footer>
            </div>

            <ChatSettingsModal
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                settings={settings}
                onSettingsChange={onSettingsChange}
                sessions={sessions}
                onClearHistory={() => {
                    onClearAllSessions();
                    setIsSettingsOpen(false);
                }}
            />

            {drugToAdd && (
                <AddDrugModal
                    drugName={drugToAdd}
                    onClose={() => setDrugToAdd(null)}
                    onDrugAdded={handleDrugAddedAndClose}
                />
            )}
        </div>
    );
};

export default Chat;